import React, { useEffect, useRef } from "react"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3"
import { useTranslation } from "react-i18next"

import { FormControl, FormHelperText, TextField } from "@material-ui/core"
import { useFormik, FormikProvider, Field } from "formik"
import * as Yup from "yup"

import countries from "../../data/all-countries.json"
import { getContactUsEndpoint } from "../../helpers/general"
import SelectForm from "../SelectForm/SelectForm"
import Button from "../buttons/Button"

import styles from "../ContactUs/ContactUs.module.scss"
import "../ContactUs/ContactUsForm.scss"

const sendContactForm = async data => {
  try {
    const res = await fetch(getContactUsEndpoint(), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    })

    const responseJson = await res.json().catch(() => ({}))

    if (!res.ok) {
      throw new Error(
        responseJson.message || "Error desconocido en el servidor"
      )
    }

    return responseJson
  } catch {
    throw new Error("Network error: No se pudo conectar con el servidor")
  }
}

const ContactUsForm = ({ onSuccess, onError }) => {
  const { t, i18n } = useTranslation()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const currentLang = i18n.language
  const currentPage = window.location.href
  const isMounted = useRef(true)
  const recaptchaEnabled = process.env.REACT_APP_RECAPTCHA_ENABLED === "true"

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  const getLabel = key => t(key)

  const countryOptions = countries
    .filter(country => !country.hidden)
    .slice()
    .sort((a, b) =>
      (a.name[currentLang] || a.name.en).localeCompare(
        b.name[currentLang] || b.name.en
      )
    )
    .map(country => ({
      value: country.code,
      label: country.name[currentLang] || country.name.en,
    }))

  const labels = {
    firstName: getLabel("firstName"),
    lastName: getLabel("lastName"),
    phone: getLabel("phone"),
    email: getLabel("email"),
    country: getLabel("country"),
    message: getLabel("message"),
    accept: getLabel("formData.privacityText"),
    marketing: getLabel("marketing"),
  }

  const validationSchema = Yup.object({
    firstName: Yup.string().required(getLabel("formData.requiredfield")),
    lastName: Yup.string().required(getLabel("formData.requiredfield")),
    phone: Yup.string().matches(
      /^\+?[0-9\s\-()]{7,20}$/,
      getLabel("Número de teléfono inválido")
    ),
    email: Yup.string()
      .email(getLabel("Correo electrónico inválido"))
      .required(getLabel("formData.requiredfield")),
    country: Yup.string().required(getLabel("formData.requiredfield")),
    accept: Yup.bool().oneOf([true], getLabel("Debes aceptar los términos")),
  })

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      company:"",
      phone: "",
      email: "",
      country: "",
      message: "",
      accept: false,
      marketing: false,
    },
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      if (!executeRecaptcha) {
        console.error("Recaptcha not yet available")
        if (isMounted.current) setSubmitting(false)
        return
      }

      try {
        const recaptchaToken = recaptchaEnabled
          ? await executeRecaptcha("contact_form_submit")
          : null
        if (!isMounted.current) return

        const payload = {
          values,
          labels,
          language: currentLang,
          currentPage: currentPage,
          recaptchaToken,
        }
        const res = await sendContactForm(payload)
        if (!isMounted.current) return

        if (res.success) {
          window.dataLayer = window.dataLayer || []
          window.dataLayer.push({
            event: "generate_lead",
            contact_type: "ev_charger_form",
            contact_reason: "Contact us",
          })
          const msg = t(res.message) || t("Formulario enviado correctamente")
          if (!isMounted.current) return
          if (onSuccess) onSuccess(msg)
        } else {
          const msg = t(res.error?.message) || t("Ha ocurrido un error")
          if (!isMounted.current) return
          if (onError) onError(msg)
        }
      } catch (error) {
        const msg = error?.message || t("Ha ocurrido un error")
        if (!isMounted.current) return
        if (onError) onError(msg)
      } finally {
        if (!isMounted.current) return
        setSubmitting(false)
      }
    },
  })

  return (
    <FormikProvider value={formik}>
      <form className={styles.content} onSubmit={formik.handleSubmit}>
        <h2 className={styles.h2}>{t("contacta")}</h2>
        <hr />

        <div className={styles.contentTop}>
          <div className={styles.col}>
            <TextField
              className={styles.input}
              label={labels.firstName}
              variant="filled"
              name="firstName"
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.firstName && Boolean(formik.errors.firstName)
              }
              helperText={formik.touched.firstName && formik.errors.firstName}
            />
          </div>

          <div className={styles.col}>
            <TextField
              className={styles.input}
              label={labels.lastName}
              variant="filled"
              name="lastName"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.lastName && Boolean(formik.errors.lastName)}
              helperText={formik.touched.lastName && formik.errors.lastName}
            />
          </div>

          <div className={styles.col}>
              <TextField
                className={styles.input}
                id="empresa"
                label={t("formData.Company")}
                variant="filled"
                name="company"
                value={formik.values.company}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.company &&
                  Boolean(formik.errors.company)
                }
                helperText={
                  formik.touched.company &&
                  formik.errors.company
                }
              />
            </div>

          <div className={styles.col}>
            <TextField
              className={styles.input}
              label={labels.phone}
              variant="filled"
              name="phone"
              type="tel"
              value={formik.values.phone}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.phone && Boolean(formik.errors.phone)}
              helperText={formik.touched.phone && formik.errors.phone}
            />
          </div>

          <div className={styles.col}>
            <TextField
              className={styles.input}
              label={labels.email}
              variant="filled"
              name="email"
              type="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
          </div>

          <div className={styles.col}>
            <FormControl className={styles.input}>
              <Field
                name="country"
                component={SelectForm}
                placeholder={labels.country}
                options={countryOptions}
              />
              {formik.touched.country && formik.errors.country && (
                <FormHelperText variant="filled" required error>
                  {formik.errors.country}
                </FormHelperText>
              )}
            </FormControl>
          </div>
        </div>

        <div className={styles.contentBottom}>
          <div className={styles.col}>
            <TextField
              className={styles.textarea}
              label={labels.message}
              variant="filled"
              name="message"
              multiline
              minRows={2}
              value={formik.values.message}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </div>

          <div className={[styles.col, styles.consulta].join(" ")}>
            <div className={[styles.contentOptions, styles.column].join(" ")}>
              <label
                className={[
                  styles.label,
                  formik.values.accept ? styles.checked : "",
                ].join(" ")}
              >
                <span className={styles.checkmark}></span>
                <span className={styles.labeltext}>
                  {labels.accept} *
                  {formik.touched.accept && formik.errors.accept && (
                    <FormHelperText error>
                      {formik.errors.accept}
                    </FormHelperText>
                  )}
                </span>
                <Field className={styles.input} type="checkbox" name="accept" />
              </label>

              <label
                className={[
                  styles.label,
                  formik.values.marketing ? styles.checked : "",
                ].join(" ")}
              >
                <span className={styles.checkmark}></span>
                <span className={styles.labeltext}>{labels.marketing}</span>
                <Field
                  className={styles.input}
                  type="checkbox"
                  name="marketing"
                />
              </label>
            </div>
          </div>

          <div className={styles.col}>
            <Button primary={true} type="submit" disabled={formik.isSubmitting}>
              {formik.isSubmitting ? t("Enviando...") : t("Enviar")}
            </Button>
          </div>
        </div>
      </form>
    </FormikProvider>
  )
}

export default ContactUsForm
